import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import {
  QuestionEditorComponent,
  QuestionEditorValue,
} from '../../questions/question-editor/question-editor.component';
import { toCreateDto } from '../../questions/question-editor/question-editor.mapper';

import { LearningPath } from '../../../../core/interfaces/learning-path';
import { QuestionBank } from '../../../../core/interfaces/question';
import { Theme } from '../../../../core/interfaces/theme';
import { CreateTestDto, UpdateTestDto } from '../../../../core/interfaces/test';
import { LearningService } from '../../../../core/services/learning-path.service';
import { QuestionService } from '../../../../core/services/question.service';
import { TestService } from '../../../../core/services/test.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { ProfessorService } from '../../../../core/services/professor.service';
import { Professor } from '../../../../core/interfaces/professor';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { SwitchButtonComponent } from '../../../ui/switch-button/switch-button.component';
import { MathTextComponent } from '../../../../shared/math/math-text.component';
import { toPlainText } from '../../../../shared/math/math-text';
import { selectableSubjects } from '../../../../shared/utils/selectable-subjects';

/** Une question retenue dans le test, avec son barème propre. */
interface PickedQuestion {
  question: QuestionBank;
  points: number;
}

/** Option minimale d'un sélecteur (parcours / matière). */
interface Option {
  id: number;
  label: string;
}

/** Modes de test proposés (alignés sur `testMode` côté serveur). */
const TEST_MODES = ['exam', 'evaluation', 'practice'] as const;
type TestMode = (typeof TEST_MODES)[number];

/** Barème par défaut selon la difficulté, quand la question n'en porte pas. */
function defaultPoints(question: QuestionBank): number {
  if (question.points && question.points > 0) return question.points;
  return question.difficultyLevel === 'hard'
    ? 3
    : question.difficultyLevel === 'medium'
      ? 2
      : 1;
}

/**
 * Composition d'un test.
 *
 * <p>Le professeur (ou l'admin) déclare d'abord la portée — parcours, matière,
 * puis les <em>modules</em> à évaluer — et la banque se restreint en
 * conséquence. Les matières proposées sont celles réellement affectées au
 * professeur (endpoints `professors/by-user/...`) ; l'admin voit tous les
 * parcours. Un module manquant se crée à la volée en le saisissant.</p>
 *
 * <p>Créer une question ne fait jamais quitter le test : un tiroir s'ouvre
 * par-dessus et réutilise l'éditeur de question complet.</p>
 */
@Component({
  selector: 'app-test-builder',
  standalone: true,
  imports: [MathTextComponent, TranslateModule, QuestionEditorComponent, SwitchButtonComponent],
  templateUrl: './test-builder.component.html',
  styleUrl: './test-builder.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestBuilderComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly learning = inject(LearningService);
  private readonly questions = inject(QuestionService);
  private readonly testService = inject(TestService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly professorsApi = inject(ProfessorService);

  readonly modes = TEST_MODES;

  /** Édition d'un test existant (route `.../:id`) plutôt que création. */
  readonly isEdit = signal(false);
  private editId: number | null = null;
  /** Statut du test chargé en édition — à renvoyer tel quel au back, qui
   *  retomberait sinon sur draft et dépublierait le test en silence. */
  private editStatus: string | null = null;

  /** Admin : voit tous les parcours ; sinon uniquement ceux du professeur. */
  isAdmin = false;
  private professorId: string | null = null;
  /** Parcours (admin, ou repli prof) conservés avec leurs matières embarquées. */
  private adminPaths: LearningPath[] = [];
  /** Vrai quand on affiche les affectations réelles du professeur (pas le repli). */
  private usingProfessorScope = false;

  /* ── Portée : parcours → matière → modules ───────────────── */
  readonly paths = signal<Option[]>([]);
  readonly pathId = signal<number | null>(null);
  readonly subjects = signal<Option[]>([]);
  readonly subjectId = signal<number | null>(null);
  readonly subjectName = signal('');
  readonly loadingScope = signal(false);

  /**
   * Professeurs affectés à la matière retenue, et celui qui sera titulaire du
   * test. Pour l'admin seul.
   *
   * <p>Un test appartient au professeur qui l'enseignera : il apparaît dans sa
   * liste, c'est lui qui l'ouvre et le valide. L'admin n'en est pas un —
   * l'écran envoyait pourtant son identité d'utilisateur, à défaut de quoi
   * <c>0</c>, et le test partait sans titulaire réel.</p>
   */
  readonly professors = signal<Professor[]>([]);
  readonly ownerId = signal<number | null>(null);
  readonly loadingProfessors = signal(false);

  /** Modules (thèmes) de la matière ; la banque s'y restreint. */
  readonly chapters = signal<Theme[]>([]);
  readonly selectedThemeIds = signal<number[]>([]);
  /** Saisie d'un nouveau module (création à la volée). */
  readonly moduleDraft = signal('');
  readonly addingModule = signal(false);

  /* ── Paramètres du test ──────────────────────────────────── */
  readonly title = signal('');
  readonly description = signal('');
  readonly mode = signal<TestMode>('exam');
  /** Fenêtre de disponibilité (datetime-local) ; facultative. */
  readonly startDate = signal('');
  readonly endDate = signal('');
  /**
   * Barème de restitution : la note se lit sur ce total, quel que soit le
   * poids des questions. 20 par défaut — les points restent un poids relatif,
   * ils ne sont jamais répartis pour tomber juste.
   */
  readonly gradingScale = signal<number | null>(20);
  /** Score requis ; null → moitié du total (calculé). */
  /**
   * Barre de réussite **exprimée sur le barème** — 10 sur 20, pas 10 %.
   *
   * C'est l'unité dans laquelle un professeur pense, et celle que disait déjà
   * l'intitulé. L'API, elle, attend un pourcentage : la conversion se fait à
   * l'envoi, via `passingPercentage`. Avant, la valeur partait telle quelle et
   * « 10 » posait un seuil à 10 %, que presque tout le monde franchissait.
   */
  readonly passingOnScale = signal<number | null>(null);
  /** Durée en minutes ; null → somme des durées de questions (calculée). */
  readonly timeLimitMinutes = signal<number | null>(null);
  readonly maxAttempts = signal<number | null>(3);
  readonly showResultsImmediately = signal(true);
  readonly allowReview = signal(false);

  /**
   * Change le mode et repositionne le défaut (éditable) de « autoriser la
   * révision » : activée pour un entraînement (تدريب), désactivée pour un examen
   * ou un test diagnostic (تشخيصي). L'utilisateur peut toujours la basculer.
   */
  setMode(mode: TestMode): void {
    this.mode.set(mode);
    this.allowReview.set(mode === 'practice');
  }

  /** Barème requis par défaut (moitié du total), proposé quand non saisi. */
  /** La moyenne du barème : 10 sur 20. */
  readonly defaultPassing = computed(() => Math.ceil((this.gradingScale() ?? 20) / 2));

  /** Ce que l'API recevra, pour l'afficher à côté de la saisie. */
  readonly passingPercentage = computed(() => {
    const scale = this.gradingScale() || 20;
    const onScale = this.passingOnScale() ?? this.defaultPassing();
    return Math.max(0, Math.min(100, Math.round((onScale / scale) * 100)));
  });

  /** Renseigne un signal numérique depuis un champ `<input type="number">`. */
  onNumber(target: WritableSignal<number | null>, raw: string): void {
    const value = raw.trim();
    target.set(value === '' ? null : Number(value));
  }

  /* ── Banque ──────────────────────────────────────────────── */
  readonly bank = signal<QuestionBank[]>([]);
  readonly bankLoading = signal(false);
  readonly bankErrored = signal(false);
  readonly search = signal('');
  readonly difficultyFilter = signal<string | null>(null);

  /* ── Sélection ───────────────────────────────────────────── */
  readonly picked = signal<PickedQuestion[]>([]);

  /* ── Tiroir de création ──────────────────────────────────── */
  readonly drawerOpen = signal(false);
  readonly drawerThemeId = signal<number | null>(null);
  readonly saving = signal(false);

  /* ── État dérivé ─────────────────────────────────────────── */
  readonly selectedChapters = computed<Theme[]>(() =>
    this.chapters().filter((c) => this.selectedThemeIds().includes(c.id)),
  );

  readonly visibleBank = computed<QuestionBank[]>(() => {
    const needle = this.search().trim().toLowerCase();
    const diff = this.difficultyFilter();
    return this.bank().filter(
      (q) =>
        (!diff || q.difficultyLevel === diff) &&
        (!needle || searchableTitle(q.title).includes(needle)),
    );
  });

  readonly totalPoints = computed(() =>
    this.picked().reduce((sum, p) => sum + p.points, 0),
  );

  readonly totalMinutes = computed(() => {
    const seconds = this.picked().reduce(
      (sum, p) => sum + (p.question.timeLimitSeconds ?? 0),
      0,
    );
    return Math.ceil(seconds / 60);
  });

  readonly coveredCount = computed(
    () => new Set(this.picked().map((p) => p.question.themeId)).size,
  );

  readonly incomplete = computed(
    () =>
      this.selectedThemeIds().length > 0 &&
      this.coveredCount() < this.selectedThemeIds().length,
  );

  readonly canSubmit = computed(
    () =>
      this.subjectId() !== null &&
      this.title().trim().length > 0 &&
      this.picked().length > 0 &&
      !this.incomplete() &&
      // L'admin doit désigner le titulaire : lui-même n'en est pas un.
      (!this.isAdmin || this.ownerId() !== null) &&
      !this.saving(),
  );

  /** Vrai quand il ne manque que le titulaire — pour le dire, plutôt que griser. */
  readonly ownerMissing = computed(
    () => this.isAdmin && this.subjectId() !== null && this.ownerId() === null,
  );

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? Number(idParam) : NaN;
    if (idParam && !Number.isNaN(id)) {
      this.editId = id;
      this.isEdit.set(true);
      void this.loadForEdit(id);
    } else {
      void this.loadScope();
    }
  }

  /**
   * Précharge un test existant dans le compositeur (mode édition). On charge la
   * taxonomie complète pour retrouver le parcours de la matière, puis on remplit
   * tous les paramètres, les modules retenus et les questions déjà dans le test.
   */
  private async loadForEdit(id: number): Promise<void> {
    this.loadingScope.set(true);
    this.isAdmin = this.auth.isAdmin();
    this.professorId = await this.auth.getUserId();
    this.usingProfessorScope = false;

    try {
      // Le référentiel entier : le test à modifier peut porter une matière
      // désactivée depuis, et il faut encore retrouver son parcours.
      const pathsRes = await firstValueFrom(this.learning.getReferential());
      this.adminPaths = pathsRes?.data ?? [];
      this.paths.set(this.adminPaths.map((p) => ({ id: p.id, label: p.title })));

      const testRes = await firstValueFrom(
        this.testService.getTestWithQuestions(id),
      );
      const test = testRes?.status === 'SUCCESS' ? testRes.data : null;
      if (!test) {
        this.swal.error(this.translate.instant('TEST_BUILDER.LOAD_ERROR'));
        return;
      }

      // Paramètres.
      this.editStatus = test.status ?? null;
      this.title.set(test.title ?? '');
      this.description.set(test.description ?? '');
      this.mode.set((test.testMode as TestMode) || 'exam');
      this.startDate.set(this.toLocalInput(test.startDate));
      this.endDate.set(this.toLocalInput(test.endDate));
      this.gradingScale.set(test.gradingScale ?? 20);
      // Le serveur stocke un pourcentage ; on le repose sur le barème pour
      // que le professeur retrouve la note qu'il avait saisie.
      const loadedScale = test.gradingScale ?? 20;
      this.passingOnScale.set(
        test.passingPercentage == null
          ? null
          : Math.round((test.passingPercentage / 100) * loadedScale),
      );
      this.timeLimitMinutes.set(test.timeLimitMinutes ?? null);
      this.maxAttempts.set(test.maxAttempts ?? 1);
      this.showResultsImmediately.set(!!test.showResultsImmediately);
      this.allowReview.set(!!test.allowReview);

      // Portée : parcours déduit de la matière.
      const path = this.adminPaths.find((p) =>
        (p.subjects ?? []).some((s) => s.id === test.subjectId),
      );
      if (path) {
        this.pathId.set(path.id);
        // La matière du test reste proposée même inactive ; les autres
        // matières inactives, elles, ne se choisissent pas.
        this.subjects.set(
          selectableSubjects(path.subjects, [test.subjectId]).map((s) => ({ id: s.id, label: s.name })),
        );
      }
      this.subjectId.set(test.subjectId);
      if (this.isAdmin) this.restoreOwner(test.subjectId, test.professorId);
      this.subjectName.set(
        test.subjectName ||
          this.subjects().find((s) => s.id === test.subjectId)?.label ||
          '',
      );

      // Modules de la matière + modules retenus par le test.
      const themesRes = await firstValueFrom(
        this.learning.getThemesBySubject(test.subjectId),
      );
      this.chapters.set(themesRes?.data ?? []);
      const questions = test.testQuestions ?? [];
      const themeIds =
        test.themeIds && test.themeIds.length
          ? test.themeIds
          : Array.from(
              new Set(
                questions
                  .map((tq) => tq.question?.themeId)
                  .filter((v): v is number => typeof v === 'number'),
              ),
            );
      this.selectedThemeIds.set(themeIds);

      // Banque (pool pour ajouter d'autres questions) + questions déjà retenues.
      this.loadBank(themeIds);
      this.picked.set(
        questions
          .filter((tq) => tq.question)
          .slice()
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map((tq) => ({
            question: tq.question,
            points:
              tq.question.points && tq.question.points > 0
                ? tq.question.points
                : defaultPoints(tq.question),
          })),
      );
    } catch {
      this.swal.error(this.translate.instant('TEST_BUILDER.LOAD_ERROR'));
    } finally {
      this.loadingScope.set(false);
    }
  }

  /** ISO → valeur `datetime-local` (`yyyy-MM-ddTHH:mm`) en heure locale. */
  private toLocalInput(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  /* ── Portée ──────────────────────────────────────────────── */

  private async loadScope(): Promise<void> {
    this.loadingScope.set(true);
    this.isAdmin = this.auth.isAdmin();

    // Admin : tous les parcours (matières embarquées).
    if (this.isAdmin) {
      this.loadAllPaths();
      return;
    }

    // Professeur : uniquement ses affectations (parcours + matières). Repli sur
    // tous les parcours s'il n'a encore aucune affectation.
    this.professorId = await this.auth.getUserId();
    if (!this.professorId) {
      this.loadingScope.set(false);
      return;
    }
    this.learning.getProfessorLearningPath(this.professorId).subscribe({
      next: (res) => {
        const paths = res?.data ?? [];
        if (paths.length > 0) {
          this.usingProfessorScope = true;
          this.paths.set(paths.map((p) => ({ id: p.id, label: p.title })));
          this.loadingScope.set(false);
        } else {
          this.loadAllPaths();
        }
      },
      error: () => this.loadAllPaths(),
    });
  }

  /** Repli : tous les parcours avec matières embarquées (admin ou prof sans affectation). */
  private loadAllPaths(): void {
    this.usingProfessorScope = false;
    this.learning.getLearningPaths(true, false).subscribe({
      next: (res) => {
        this.adminPaths = res?.data ?? [];
        this.paths.set(this.adminPaths.map((p) => ({ id: p.id, label: p.title })));
        this.loadingScope.set(false);
      },
      error: () => this.loadingScope.set(false),
    });
  }

  onPathChange(raw: string): void {
    const id = Number(raw) || null;
    this.pathId.set(id);
    this.resetFromSubject();
    this.subjects.set([]);
    if (id === null) return;

    // Professeur scopé : ses matières affectées pour ce parcours.
    if (this.usingProfessorScope && this.professorId) {
      this.learning.getProfessorSubjects(this.professorId, id).subscribe({
        next: (res) => {
          // Le parcours a pu changer pendant la requête : ses matières
          // n'appartiennent plus à celui qui est affiché.
          if (this.pathId() !== id) return;
          this.subjects.set(
            (res?.data ?? []).map((s) => ({ id: s.id, label: s.name })),
          );
        },
        error: () => {
          if (this.pathId() === id) this.subjects.set([]);
        },
      });
      return;
    }

    // Admin ou repli : matières embarquées dans le parcours. En édition, ce
    // référentiel porte aussi les matières inactives : changer de parcours ne
    // doit pas les proposer.
    const path = this.adminPaths.find((p) => p.id === id);
    this.subjects.set(
      selectableSubjects(path?.subjects).map((s) => ({ id: s.id, label: s.name })),
    );
  }

  onSubjectChange(raw: string): void {
    const id = Number(raw) || null;
    this.subjectId.set(id);
    this.subjectName.set(this.subjects().find((s) => s.id === id)?.label ?? '');
    this.resetFromChapters();
    this.loadProfessors(id);
    if (id !== null) this.loadChapters(id);
  }

  /**
   * Charge les professeurs affectés à la matière. Le titulaire précédent est
   * oublié : garder celui d'une autre matière attribuerait le test à quelqu'un
   * qui n'y enseigne pas.
   */
  private loadProfessors(subjectId: number | null): void {
    this.ownerId.set(null);
    this.professors.set([]);
    // La réponse d'une matière quittée est ignorée : c'est ici, au changement,
    // que son indicateur de chargement se relâche.
    this.loadingProfessors.set(false);

    if (subjectId === null || !this.isAdmin) return;

    this.loadingProfessors.set(true);
    this.professorsApi.getProfessors(subjectId).subscribe({
      next: (res) => {
        // Réponse d'une matière quittée entre-temps : la garder désignerait
        // d'office, comme titulaire, un professeur qui n'enseigne pas celle
        // qui est affichée.
        if (this.subjectId() !== subjectId) return;
        const list = res?.data ?? [];
        this.professors.set(list);

        // Un seul professeur sur la matière : le choix n'en est pas un.
        if (list.length === 1) this.ownerId.set(Number(list[0].id));
        this.loadingProfessors.set(false);
      },
      error: () => {
        if (this.subjectId() !== subjectId) return;
        this.professors.set([]);
        this.loadingProfessors.set(false);
      },
    });
  }

  /**
   * Reprend le titulaire du test ouvert en modification.
   *
   * <p>Sans cela, l’admin devrait le rechoisir à chaque retouche, et un oubli
   * l’aurait remplacé en silence.</p>
   */
  private restoreOwner(subjectId: number, professorId: string | number | null | undefined): void {
    this.professorsApi.getProfessors(subjectId).subscribe({
      next: (res) => {
        // La matière a pu être changée avant la réponse : restaurer alors le
        // titulaire d'origine l'attribuerait à une matière qu'il n'enseigne pas.
        if (this.subjectId() !== subjectId) return;
        this.professors.set(res?.data ?? []);
        if (professorId) this.ownerId.set(Number(professorId));
      },
      error: () => {
        if (this.subjectId() === subjectId) this.professors.set([]);
      },
    });
  }

  onOwnerChange(raw: string): void {
    this.ownerId.set(Number(raw) || null);
  }

  professorLabel(p: Professor): string {
    return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.email;
  }

  private loadChapters(subjectId: number): void {
    this.learning.getThemesBySubject(subjectId).subscribe({
      next: (res) => {
        // Les chapitres d'une matière quittée entre-temps ne doivent pas
        // s'afficher sous la nouvelle : on composerait un test hors sujet.
        if (this.subjectId() === subjectId) this.chapters.set(res?.data ?? []);
      },
      error: () => {
        if (this.subjectId() === subjectId) this.chapters.set([]);
      },
    });
  }

  /** Sélectionne le module s'il existe déjà, sinon le crée après confirmation. */
  async addModule(): Promise<void> {
    const title = this.moduleDraft().trim();
    const subjectId = this.subjectId();
    if (!title || subjectId === null || this.addingModule()) return;

    const existing = this.chapters().find(
      (c) => (c.title ?? '').trim().toLowerCase() === title.toLowerCase(),
    );
    if (existing) {
      this.moduleDraft.set('');
      if (!this.isChapterSelected(existing.id)) this.toggleChapter(existing.id);
      return;
    }

    const ok = await this.swal.confirmAsync(
      this.translate.instant('TEST_BUILDER.ADD_MODULE_CONFIRM', { name: title }),
    );
    if (!ok) return;

    this.addingModule.set(true);
    this.learning
      .createTheme({
        title,
        description: '',
        subjectId,
        displayOrder: this.chapters().length,
        isActive: true,
      })
      .subscribe({
        next: (res) => {
          this.addingModule.set(false);
          const created = res?.data;
          if (created) {
            this.chapters.set([...this.chapters(), created]);
            this.moduleDraft.set('');
            this.toggleChapter(created.id);
          }
        },
        error: () => this.addingModule.set(false),
      });
  }

  toggleChapter(themeId: number): void {
    const current = this.selectedThemeIds();
    const next = current.includes(themeId)
      ? current.filter((id) => id !== themeId)
      : [...current, themeId];
    this.selectedThemeIds.set(next);
    this.loadBank(next);
  }

  isChapterSelected(themeId: number): boolean {
    return this.selectedThemeIds().includes(themeId);
  }

  private resetFromSubject(): void {
    this.subjectId.set(null);
    this.subjectName.set('');
    // Sans matière, plus de professeurs à proposer ni de titulaire : ceux de la
    // matière quittée ne valent pas pour la suivante.
    this.loadProfessors(null);
    this.resetFromChapters();
  }

  private resetFromChapters(): void {
    this.chapters.set([]);
    this.selectedThemeIds.set([]);
    this.moduleDraft.set('');
    // Une lecture de la banque encore en vol appartient à la portée quittée :
    // avancer le rang l'empêche de la repeupler après coup.
    this.bankRequest++;
    this.bank.set([]);
    this.bankLoading.set(false);
    this.bankErrored.set(false);
    this.picked.set([]);
    this.search.set('');
    this.difficultyFilter.set(null);
  }

  /* ── Banque ──────────────────────────────────────────────── */

  /** Rang de la dernière lecture de la banque : seule sa réponse compte. */
  private bankRequest = 0;

  private loadBank(themeIds: number[]): void {
    // Cocher deux chapitres coup sur coup lance deux lectures. Sans ce rang,
    // la première — plus étroite — pouvait arriver en dernier et retirer de la
    // banque les questions du second chapitre, pourtant coché.
    const request = ++this.bankRequest;

    if (themeIds.length === 0) {
      this.bank.set([]);
      this.bankErrored.set(false);
      this.bankLoading.set(false);
      return;
    }
    this.bankLoading.set(true);
    this.bankErrored.set(false);

    this.questions.getQuestionsByThemeIds(themeIds).subscribe({
      next: (res) => {
        if (request !== this.bankRequest) return;
        this.bank.set(res?.status === 'SUCCESS' ? (res.data ?? []) : []);
        this.bankErrored.set(res?.status !== 'SUCCESS');
        this.bankLoading.set(false);
      },
      error: () => {
        if (request !== this.bankRequest) return;
        this.bank.set([]);
        this.bankErrored.set(true);
        this.bankLoading.set(false);
      },
    });
  }

  reloadBank(): void {
    this.loadBank(this.selectedThemeIds());
  }

  onSearch(value: string): void {
    this.search.set(value);
  }

  toggleDifficulty(level: string): void {
    this.difficultyFilter.set(this.difficultyFilter() === level ? null : level);
  }

  isPicked(questionId: number): boolean {
    return this.picked().some((p) => p.question.id === questionId);
  }

  /* ── Sélection ───────────────────────────────────────────── */

  togglePick(question: QuestionBank): void {
    const current = this.picked();
    const at = current.findIndex((p) => p.question.id === question.id);
    if (at >= 0) {
      this.picked.set(current.filter((_, i) => i !== at));
    } else {
      this.picked.set([...current, { question, points: defaultPoints(question) }]);
    }
  }

  setPoints(questionId: number, raw: string): void {
    const points = Math.max(1, Number(raw) || 1);
    this.picked.set(
      this.picked().map((p) =>
        p.question.id === questionId ? { ...p, points } : p,
      ),
    );
  }

  move(index: number, delta: number): void {
    const list = [...this.picked()];
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    this.picked.set(list);
  }

  remove(questionId: number): void {
    this.picked.set(this.picked().filter((p) => p.question.id !== questionId));
  }

  autoSelect(): void {
    const current = [...this.picked()];
    const pickedIds = new Set(current.map((p) => p.question.id));
    for (const q of this.visibleBank().slice(0, 5)) {
      if (!pickedIds.has(q.id)) current.push({ question: q, points: defaultPoints(q) });
    }
    this.picked.set(current);
  }

  /* ── Tiroir de création ──────────────────────────────────── */

  openDrawer(): void {
    const chapters = this.selectedThemeIds();
    if (chapters.length === 0) return;
    this.drawerThemeId.set(chapters[0]);
    this.drawerOpen.set(true);
  }

  closeDrawer(): void {
    this.drawerOpen.set(false);
  }

  onDrawerThemeChange(raw: string): void {
    this.drawerThemeId.set(Number(raw) || null);
  }

  onQuestionCreated(value: QuestionEditorValue): void {
    const themeId = this.drawerThemeId();
    if (themeId === null || this.saving()) return;
    this.saving.set(true);

    this.questions.create(toCreateDto(value, themeId)).subscribe({
      next: (res) => {
        this.saving.set(false);
        const created = res?.status === 'SUCCESS' ? res.data : null;
        if (created) {
          this.bank.set([created, ...this.bank()]);
          this.picked.set([
            ...this.picked(),
            { question: created, points: defaultPoints(created) },
          ]);
        }
        this.closeDrawer();
      },
      error: () => this.saving.set(false),
    });
  }

  /* ── Enregistrement du test ──────────────────────────────── */

  submit(): void {
    const subjectId = this.subjectId();
    if (!this.canSubmit() || subjectId === null) return;
    this.saving.set(true);

    const picked = this.picked();
    const base: CreateTestDto = {
      title: this.title().trim(),
      description: this.description().trim(),
      // Pour un professeur, le serveur dérive l'identité du JWT ; on n'envoie
      // donc pas d'id significatif ici. Pour l'admin, en revanche, il n'y a
      // aucune identité à dériver : c'est le titulaire choisi qui compte.
      professorId: this.isAdmin
        ? this.ownerId()!
        : Number(this.professorId) || 0,
      subjectId,
      themeIds: this.selectedThemeIds(),
      totalPoints: this.totalPoints(),
      questionsCount: picked.length,
      gradingScale: this.gradingScale() ?? undefined,
      passingPercentage: this.passingPercentage(),
      testMode: this.mode(),
      // La durée saisie, ou aucune. Déduite des limites par question, elle
      // faisait d'un diagnostic à une question chronométrée un test d'une
      // minute ; et 0 devient côté serveur une échéance immédiate.
      timeLimitMinutes: this.timeLimitMinutes() || null,
      maxAttempts: this.maxAttempts() || 1,
      showResultsImmediately: this.showResultsImmediately(),
      allowReview: this.allowReview(),
      startDate: this.startDate()
        ? new Date(this.startDate()).toISOString()
        : undefined,
      endDate: this.endDate()
        ? new Date(this.endDate()).toISOString()
        : undefined,
      questionIds: picked.map((p) => p.question.id),
    };

    // Édition : on met à jour le test existant puis on revient à la liste.
    if (this.isEdit() && this.editId !== null) {
      const dto: UpdateTestDto = {
        ...base,
        id: this.editId,
        status: this.editStatus ?? 'draft',
      };
      this.testService.update(this.editId, dto).subscribe({
        next: (res) => {
          this.saving.set(false);
          if (res?.status === 'SUCCESS') {
            this.swal.success(
              this.translate.instant('TEST_BUILDER.UPDATE_SUCCESS'),
            );
            this.router.navigate([this.returnUrl()]);
          } else {
            this.swal.error(
              res?.message ??
                this.translate.instant('TEST_BUILDER.UPDATE_ERROR'),
            );
          }
        },
        error: () => {
          this.saving.set(false);
          this.swal.error(this.translate.instant('TEST_BUILDER.UPDATE_ERROR'));
        },
      });
      return;
    }

    this.testService.createTest(base).subscribe({
      next: (res) => {
        this.saving.set(false);
        if (res?.status === 'SUCCESS') {
          this.swal.success(this.translate.instant('TEST_BUILDER.CREATE_SUCCESS'));
          // On reste sur la page pour enchaîner un autre test sur la même
          // portée (parcours/matière/modules conservés) : seuls le titre et la
          // sélection de questions repartent à zéro. Créer plusieurs tests se
          // fait ainsi sans changer d'écran ni recharger la banque.
          this.title.set('');
          this.description.set('');
          this.passingOnScale.set(null);
          this.timeLimitMinutes.set(null);
          this.picked.set([]);
        } else {
          this.swal.error(
            res?.message ?? this.translate.instant('TEST_BUILDER.CREATE_ERROR'),
          );
        }
      },
      error: () => {
        this.saving.set(false);
        this.swal.error(this.translate.instant('TEST_BUILDER.CREATE_ERROR'));
      },
    });
  }

  cancel(): void {
    this.router.navigate([this.returnUrl()]);
  }

  /**
   * Où l'on revient après enregistrement ou abandon.
   *
   * <p>On repart d'où l'on vient : l'édition d'un test se lance depuis une
   * liste de tests, la composition depuis la banque de questions. Côté
   * enseignant les deux se confondent — `tests/bank` est sa liste ; côté
   * administration elles sont distinctes, et renvoyer une correction de test
   * vers la banque de questions ferait perdre les filtres de la liste.</p>
   */
  private returnUrl(): string {
    if (!this.isAdmin) return '/teacher/tests/bank';
    return this.isEdit() ? '/admin/tests' : '/admin/question-bank';
  }

  /* ── Utilitaires de gabarit ──────────────────────────────── */

  chapterName(themeId: number | undefined): string {
    return this.chapters().find((c) => c.id === themeId)?.title ?? '—';
  }

  modeKey(mode: TestMode): string {
    return `TEST_BUILDER.MODE_${mode.toUpperCase()}`;
  }

  diffKey(level: string): string {
    return `TEST_BUILDER.DIFF_${level.toUpperCase()}`;
  }
}

/**
 * Titre tel qu'on le cherche : la source, plus sa lecture à plat ramenée aux
 * caractères simples. « R1 » trouve ainsi un énoncé écrit `\(\mathrm{R}_{1}\)`.
 */
function searchableTitle(title: string | null | undefined): string {
  const source = title ?? '';
  return `${source} ${toPlainText(source).normalize('NFKC')}`.toLowerCase();
}
